import { zodResolver } from "@hookform/resolvers/zod"
import { motion } from "framer-motion"
import * as _ from "lodash"
import { useTranslations } from "next-intl"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { v4 as uuidv4 } from "uuid"

import { PostalCodeInput } from "@/components/shared/PostalCodeInput"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Link } from "@/i18n/routing"
import { cn } from "@/lib/utils"
import type { MerchantType, UserPreferredLanguage, VendorType } from "@/lib/zenstack/models"
import { ROUTES } from "@/routes"
import {
  MerchantProfileSchemas,
  type TMerchantProfileCreateData,
  type TMerchantProfileUpdateData,
} from "@/schemas/merchant/MerchantProfileSchemas"
import { ClientUtils } from "@/utils/client/ClientUtils"

import { LocalLangSwitch } from "../LocalLangSwitch"
import FormFieldError from "../shared/FormFieldError"
import { TextHint } from "../TextHint"
import { DiscountPercentageInput } from "./DiscountPercentageInput"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { CONST } from "@/constants"
export interface MerchantProfileFormData {
  profile?: TMerchantProfileCreateData
}

interface MerchantProfileFormProps {
  merchantType: MerchantType
  merchantProfile?: TMerchantProfileCreateData
  onSubmit: ((data: TMerchantProfileCreateData) => void) | ((data: TMerchantProfileUpdateData) => void)
  isLoading: boolean
  loadingProgress: number
  showUpdatePassword?: boolean
}

export function MerchantProfileForm({
  merchantType,
  merchantProfile,
  onSubmit,
  isLoading,
  loadingProgress,
  showUpdatePassword,
}: MerchantProfileFormProps) {
  const t = useTranslations("merchant.profile")
  const tMerchant = useTranslations("merchant")
  const tCommon = useTranslations("common")
  const [fieldLang, setFieldLang] = useState<UserPreferredLanguage>("en")

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isDirty, dirtyFields },
  } = useForm({
    resolver: zodResolver(
      merchantProfile ? MerchantProfileSchemas.update(tMerchant) : MerchantProfileSchemas.create(tMerchant)
    ),
    defaultValues: {
      id: uuidv4(), // Arbitrary id to avoid zod validation error, will be overridden by the backend
      // Skip fields that are not allowed to be updated
      ..._.pick(merchantProfile, [
        "id",
        "name",
        "street",
        "city",
        "state",
        "zip",
        "phone",
        "descriptionEN",
        "descriptionFR",
        "defaultDiscount",
        "website",
        "weeklyCouponLimit",
      ]),
      state: merchantProfile?.state ?? "QC",
    },
  })

  const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/[^\d]/g, "")
    const phoneNumberLength = phoneNumber.length
    if (phoneNumberLength < 4) return phoneNumber
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
  }

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhoneNumber = formatPhoneNumber(event.target.value)
    setValue("phone", formattedPhoneNumber, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  const submitForm = (data: TMerchantProfileCreateData | TMerchantProfileUpdateData) => {
    if (merchantProfile) {
      const dirtyData = ClientUtils.dirtyValues(dirtyFields, data)
      onSubmit(dirtyData as any)
    } else {
      onSubmit(data as any)
    }
  }

  return (
    <>
      <div>
        <Label htmlFor="langSwitch">{tCommon("lang.label")}</Label>
        <LocalLangSwitch
          value={fieldLang}
          onChange={setFieldLang}
          isFrError={!!errors?.descriptionFR?.message}
          isEnError={!!errors?.descriptionEN?.message}
        />
        <TextHint text={tCommon("lang.hint")} />
      </div>
      <form onSubmit={handleSubmit(submitForm)} className="my-4 space-y-4">
        <div className="space-y-4 pr-2">
          {/* Description Field */}
          <div>
            <Label htmlFor="description">
              {t("update.form.description.label")} <TextHint text={fieldLang.toUpperCase()} className="inline" />
            </Label>
            <Textarea
              {...register("descriptionEN", { maxLength: 1000, setValueAs: (value) => value || undefined })}
              placeholder={t("update.form.description.placeholder")}
              rows={4}
              className={cn(errors?.descriptionEN?.message && "border-destructive", fieldLang === "fr" && "hidden")}
            />
            <FormFieldError error={errors?.descriptionEN?.message} />

            <Textarea
              {...register("descriptionFR", { maxLength: 1000, setValueAs: (value) => value || undefined })}
              placeholder={t("update.form.description.placeholder")}
              rows={4}
              className={cn(errors?.descriptionFR?.message && "border-destructive", fieldLang === "en" && "hidden")}
            />
            <FormFieldError error={errors?.descriptionFR?.message} />
          </div>

          {/* Basic Info Fields */}
          <div>
            <Label htmlFor="name">{t("update.form.name.label")}</Label>
            <Input {...register("name", { required: true })} placeholder={t("update.form.name.placeholder")} required />
            <FormFieldError error={errors?.name?.message} />
          </div>

          {!merchantProfile && (
            <div>
              <Label htmlFor="vendorType">{t("update.form.vendor_type.label")}</Label>
              <Select
                {...register("vendorType", { required: true })}
                onValueChange={(value) => {
                  setValue("vendorType", value as VendorType, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  })
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("update.form.vendor_type.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  {CONST.VENDOR_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {tCommon(`vendor_type.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {/* Address Fields */}
          <div>
            <Label htmlFor="street">{t("update.form.address.street.label")}</Label>
            <Input
              {...register("street", { maxLength: 255 })}
              required
              placeholder={t("update.form.address.street.placeholder")}
              disabled={!!merchantProfile}
            />
            <FormFieldError error={errors?.street?.message} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="city">{t("update.form.address.city.label")}</Label>
              <Input
                {...register("city", { maxLength: 255 })}
                required
                placeholder={t("update.form.address.city.placeholder")}
                disabled={!!merchantProfile}
              />
              <FormFieldError error={errors?.city?.message} />
            </div>
            <div>
              <Label htmlFor="state">{t("update.form.address.state.label")}</Label>
              <Input
                {...register("state")}
                disabled
                placeholder={t("update.form.address.state.placeholder")}
                defaultValue="QC"
              />
              <FormFieldError error={errors?.state?.message} />
            </div>
          </div>

          <div>
            <Label htmlFor="zip">{t("update.form.address.zip.label")}</Label>
            <PostalCodeInput
              register={register}
              setValue={setValue}
              name="zip"
              required={!merchantProfile}
              disabled={!!merchantProfile}
            />
            <FormFieldError error={errors?.zip?.message} />
          </div>

          {/* Contact Fields */}
          <div>
            <Label htmlFor="phone">{t("update.form.phone.label")}</Label>
            <Input
              {...register("phone", { maxLength: 255 })}
              required
              type="tel"
              placeholder={t("update.form.phone.placeholder")}
              onChange={handlePhoneChange}
              maxLength={14}
            />
            <FormFieldError error={errors?.phone?.message} />
          </div>

          <div>
            <Label htmlFor="website">{t("update.form.website.label")}</Label>
            <Input
              {...register("website", {
                setValueAs: (value) => value || undefined,
                maxLength: 255,
              })}
              placeholder={t("update.form.website.placeholder")}
            />
            <FormFieldError error={errors?.website?.message} />
          </div>

          {/* Discount Fields */}
          <div>
            <Label htmlFor="defaultDiscount">{t("update.form.discount.label")}</Label>
            <DiscountPercentageInput
              {...register("defaultDiscount", { valueAsNumber: true })}
              onValueChange={(value) => {
                setValue("defaultDiscount", value ? Number(value) : undefined, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }}
            />
            <p className="mt-1 text-sm text-muted-foreground">{t("update.form.discount.hint")}</p>
            <FormFieldError error={errors?.defaultDiscount?.message} />
          </div>

          {merchantType === "COUPONS" && (
            <div>
              <Label htmlFor="weeklyCouponLimit">{t("update.form.coupon_limit.label")}</Label>
              <Input
                {...register("weeklyCouponLimit", { valueAsNumber: true })}
                placeholder={t("update.form.coupon_limit.placeholder")}
                type="number"
                min={0}
              />
              <FormFieldError error={errors?.weeklyCouponLimit?.message} />
            </div>
          )}
        </div>

        <div className="relative">
          {showUpdatePassword && (
            <>
              <Link href={ROUTES.updateEmail} className="mb-4 block text-center text-sm text-primary hover:underline">
                {tCommon("auth.email.title")}
              </Link>
              <Link
                href={ROUTES.updatePassword}
                className="mb-4 block text-center text-sm text-primary hover:underline"
              >
                {t("update.change_password")}
              </Link>
            </>
          )}
          {Object.keys(errors).length > 0 && (
            <div className="mb-4 text-center text-sm text-destructive">{t("validation.some_fields_invalid")}</div>
          )}
          <Button type="submit" className="w-full" disabled={isLoading || !isDirty || Object.keys(errors).length > 0}>
            {t("update.save_changes")}
          </Button>
          {isLoading && (
            <motion.div
              className="absolute inset-x-0 bottom-0 h-1 bg-primary"
              initial={{ width: 0 }}
              animate={{ width: `${loadingProgress}%` }}
              transition={{ duration: 0.2 }}
            />
          )}
        </div>
      </form>
    </>
  )
}
